/**
 * Доменные действия Диска (defineDriveActions).
 *
 * Модуль или инстанс регистрирует правило «эта папка моя → вот что в ней можно
 * делать»: массовые действия над её файлами и пункты контекстного меню самой
 * папки. Диск сам решает, где их показать, — разработчик описывает ТОЛЬКО
 * применимость и обработчик.
 *
 *   defineDriveActions({
 *     when: folder => folder.code?.startsWith('mws:ncr:') ?? false,
 *     fileActions: [
 *       { title: 'Привязать к NCR', handler: ({ files, refresh }) => { … } },
 *     ],
 *     folderActions: [
 *       { title: 'Сформировать отчёт', handler: ({ folder }) => { … } },
 *     ],
 *   });
 *
 * Папку опознают по системному якорю `folders.code` — он переживает
 * переименование и перенос папки юзером (см. `defineFilePlacement`, которым
 * этот якорь и проставляется). Опознавать по имени нельзя: имя юзерское.
 *
 * Регистрация — в `client/bootstrap.ts` модуля/инстанса, один раз до рендера.
 */
import type { ReactNode } from 'react';
import type { Folder } from '../../schemas/folder.schema';
import type { UploadedFile } from '../../schemas/uploaded_file.schema';

/** Контекст обработчика: что выделено, где и как обновить экран. */
export interface DriveFileActionContext {
  /**
   * Выделенные файлы. Всегда настоящие файлы: папки и ярлыки на Диске не
   * выделяются в принципе (`isRowSelectable`), поэтому разбирать `kind` или
   * разрешать ярлык в оригинал модулю не нужно.
   */
  files: UploadedFile[];
  /** Папка, в которой сделано выделение. */
  folder: Folder;
  /** Перечитать список файлов после действия. */
  refresh: () => void;
}

export interface DriveFolderActionContext {
  folder: Folder;
  /** Перечитать папки и список после действия. */
  refresh: () => void;
}

/** Массовое действие над выделенными файлами папки (тулбар таблицы). */
export interface DriveFileAction {
  title: string;
  /** Цвет кнопки Mantine — например `red` для разрушительных действий. */
  color?: string;
  icon?: ReactNode;
  handler: (ctx: DriveFileActionContext) => void | Promise<void>;
}

/** Пункт контекстного меню самой папки (дерево, строка таблицы, плитка). */
export interface DriveFolderAction {
  title: string;
  color?: string;
  handler: (ctx: DriveFolderActionContext) => void | Promise<void>;
}

export interface DriveActionsRule {
  /**
   * «Моя ли это папка». СИНХРОННЫЙ предикат только по полям папки: он
   * вызывается на каждый рендер меню и тулбара, запрос в БД тут убил бы
   * отзывчивость. Обычно — проверка якоря `folder.code`.
   */
  when: (folder: Folder) => boolean;
  /** Массовые действия над файлами внутри папки. */
  fileActions?: DriveFileAction[];
  /** Пункты контекстного меню самой папки. */
  folderActions?: DriveFolderAction[];
}

/** Реестр правил (in-memory). Наполняется модулями/инстансами в их bootstrap.ts. */
const rules: DriveActionsRule[] = [];

/** Зарегистрировать доменные действия Диска (см. DriveActionsRule). */
export function defineDriveActions(rule: DriveActionsRule): void {
  rules.push(rule);
}

/**
 * Массовые действия, применимые к папке, в порядке регистрации правил.
 * Диск дописывает их после встроенных («Переместить», «Удалить»).
 */
export function getDriveFileActions(folder: Folder | null | undefined): DriveFileAction[] {
  if (!folder) return [];
  return rules.filter(rule => rule.when(folder)).flatMap(rule => rule.fileActions ?? []);
}

/**
 * Пункты меню, применимые к папке, в порядке регистрации правил.
 * Диск показывает их отдельной группой после штатных пунктов.
 */
export function getDriveFolderActions(folder: Folder | null | undefined): DriveFolderAction[] {
  if (!folder) return [];
  return rules.filter(rule => rule.when(folder)).flatMap(rule => rule.folderActions ?? []);
}

/** Очистить реестр — только для изолированных тестов. */
export function __clearDriveActionsForTest(): void {
  rules.length = 0;
}
